/**
 * Record-a-Skill Fleet Control controller.
 */

import type { GatewayBrowserClient } from "../gateway.ts";

export type RecordSkillPrivacyNotice = {
  title: string;
  bullets: string[];
  acknowledgeLabel: string;
};

export type RecordSkillRow = {
  recordId: string;
  status: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  skillName?: string;
  publishedPath?: string;
  eventCount?: number;
};

export type RecordSkillDetail = RecordSkillRow & {
  draftPreview?: string;
  privacyNotes?: string[];
  published?: { skillMdPath: string; skillDir: string; skillName: string };
  narrationTranscript?: string;
};

export type RecordSkillState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  recordSkillLoading: boolean;
  recordSkillList: RecordSkillRow[];
  recordSkillError: string | null;
  recordSkillPrivacy: RecordSkillPrivacyNotice | null;
  recordSkillSelectedId?: string | null;
  recordSkillDetail?: RecordSkillDetail | null;
  recordSkillActiveId?: string | null;
};

export async function loadRecordSkillPrivacy(state: RecordSkillState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<{ notice: RecordSkillPrivacyNotice }>(
      "recordSkill.privacy",
      {},
    );
    state.recordSkillPrivacy = res.notice;
  } catch (err) {
    state.recordSkillError = err instanceof Error ? err.message : String(err);
  }
}

export async function loadRecordSkills(state: RecordSkillState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.recordSkillLoading = true;
  state.recordSkillError = null;
  try {
    const res = await state.client.request<{ sessions: RecordSkillRow[] }>("recordSkill.list", {});
    state.recordSkillList = res.sessions ?? [];
  } catch (err) {
    state.recordSkillError = err instanceof Error ? err.message : String(err);
  } finally {
    state.recordSkillLoading = false;
  }
}

export async function startRecordSkill(
  state: RecordSkillState,
  input: {
    title?: string;
    desktopControllerUrl?: string;
    narrationTranscript?: string;
    privacyAcknowledged: boolean;
  },
): Promise<string | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  state.recordSkillError = null;
  try {
    const res = await state.client.request<{ session: { recordId: string } }>("recordSkill.start", {
      privacyAcknowledged: input.privacyAcknowledged,
      title: input.title,
      desktopControllerUrl: input.desktopControllerUrl,
      narrationTranscript: input.narrationTranscript,
    });
    state.recordSkillActiveId = res.session?.recordId ?? null;
    await loadRecordSkills(state);
    return res.session?.recordId ?? null;
  } catch (err) {
    state.recordSkillError = err instanceof Error ? err.message : String(err);
    return null;
  }
}

export async function stopRecordSkill(
  state: RecordSkillState,
  recordId: string,
  narrationTranscript?: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("recordSkill.stop", { recordId, narrationTranscript });
    state.recordSkillActiveId = null;
    await loadRecordSkills(state);
  } catch (err) {
    state.recordSkillError = err instanceof Error ? err.message : String(err);
  }
}

export async function finalizeRecordSkill(
  state: RecordSkillState,
  recordId: string,
  opts: { title?: string; narrationTranscript?: string } = {},
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request("recordSkill.finalize", {
      recordId,
      title: opts.title,
      narrationTranscript: opts.narrationTranscript,
    });
    state.recordSkillActiveId = null;
    await loadRecordSkills(state);
    await selectRecordSkill(state, recordId);
  } catch (err) {
    state.recordSkillError = err instanceof Error ? err.message : String(err);
  }
}

export async function selectRecordSkill(
  state: RecordSkillState,
  recordId: string | null,
): Promise<void> {
  state.recordSkillSelectedId = recordId;
  state.recordSkillDetail = null;
  if (!recordId || !state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<{
      session: {
        recordId: string;
        status: string;
        title?: string;
        createdAt: string;
        updatedAt: string;
        eventCount?: number;
        draft?: { skillName: string; skillMarkdown: string };
        published?: { skillMdPath: string; skillDir: string; skillName: string };
        privacyNotes?: string[];
        narrationTranscript?: string;
      };
    }>("recordSkill.get", { recordId });
    const s = res.session;
    state.recordSkillDetail = {
      recordId: s.recordId,
      status: s.status,
      title: s.title ?? s.draft?.skillName ?? s.recordId,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
      eventCount: s.eventCount,
      skillName: s.draft?.skillName ?? s.published?.skillName,
      publishedPath: s.published?.skillMdPath,
      draftPreview: s.draft?.skillMarkdown,
      privacyNotes: s.privacyNotes,
      published: s.published,
      narrationTranscript: s.narrationTranscript,
    };
  } catch (err) {
    state.recordSkillError = err instanceof Error ? err.message : String(err);
  }
}
